import React, { useState } from 'react';
import { Box, Link, TextField, IconButton } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { DropdownFilter } from '../../../Planner/Filters/DropdownFilter';
import useTeamProvider from '../useTeamProvider';
import { useFetchOrgUnitOptions } from '../../../../actions/areas/shared/fetchOptions';
import globals from '../../../../globals/communityGlobals';
import KeyboardDatePickerWrapper from '../../../common/KeyboardDateTimeSelectors/KeyboardDateSelector';
import { getErrorProps } from '../../../../utils/keyboardSelectorFunctions';
import { isAnyDateEmpty, isDateFromBeforeDateTo, isPeriodWithinLimit } from '../../../../utils/dates';

export default function RequestLogsSearchFilters() {
  const { epRequestEnums, toDate, fromDate, getOrgUnitId, setStateVisitId, submit } = useTeamProvider();

  const [state, setState] = useState({
    orgId: getOrgUnitId,
    reqStatus: [epRequestEnums[0].id],
    visitId: '',
    fromDate,
    toDate,
  });
  const [validationErrors, setValidationErrors] = useState({});

  const defaultState = {
    reqStatus: [epRequestEnums[0].id],
    fromDate: globals.now.subtract(1, 'd'),
    toDate: globals.now.add(1, 'd'),
    orgId: getOrgUnitId,
    visitId: '',
    selectedAllForSending: false,
    requestIds: [],
  };

  const locale = globals.getAreas(['error', 'externalProvider', 'userActions']);

  const defaultOrgUnitId = getOrgUnitId;
  const maxFilterPeriodDays = 30;
  const { data: orgUnitOptions } = useFetchOrgUnitOptions(false);

  const handleClear = () => {
    setState(defaultState);
    setValidationErrors({});
    submit(defaultState);
  };

  const onSubmit = () => {
    submit(state);
  };

  const noValidationErrors = () => {
    return !validationErrors.fromDate && !validationErrors.toDate;
  };

  const areDatesValid =
    noValidationErrors() &&
    isDateFromBeforeDateTo(state.fromDate, state.toDate) &&
    isPeriodWithinLimit(state.fromDate, state.toDate, 30);

  const handleDateChange = (fieldName, date) => {
    setState(prevState => ({ ...prevState, [fieldName]: date }));

    const from = fieldName === 'fromDate' ? date : state.fromDate;
    const to = fieldName === 'toDate' ? date : state.toDate;
    const emptyFieldErrorMessage = 'Please enter date';

    if (!isPeriodWithinLimit(from, to, maxFilterPeriodDays)) {
      setValidationErrors(prevState => ({
        ...prevState,
        toDate: locale.getMsg('filterPeriodLimit', ['30', 'days']),
      }));
      return;
    }

    if (isAnyDateEmpty(from, to)) {
      setValidationErrors({
        fromDate: from ? null : emptyFieldErrorMessage,
        toDate: to ? null : emptyFieldErrorMessage,
      });
      return;
    }

    setValidationErrors({ from: null, to: null });
  };

  return (
    <React.Fragment>
      <Box p={1} width={70} textAlign="end">
        <span> {locale.getMsg('orgUnit')}</span>
      </Box>
      <Box p={1} width={200} flexGrow={1}>
        <DropdownFilter
          fullWidth
          data={orgUnitOptions}
          defaultValue={defaultOrgUnitId}
          filterKey={'orgUnit'}
          filterValue={state.orgId}
          onChange={(name, value) => setState({ ...state, orgId: value })}
        />
      </Box>
      <Box p={1} width={50} textAlign="end">
        <span> {locale.getMsg('status')}</span>
      </Box>
      <Box p={1} width={120} flexGrow={1}>
        <DropdownFilter
          data={epRequestEnums}
          filterKey={'requestStatus'}
          filterValue={state.reqStatus}
          defaultValue={state.reqStatus}
          onChange={(displayName, value) => setState({ ...state, reqStatus: value })}
        />
      </Box>
      <Box p={1} width={70} textAlign="end">
        <span> {locale.getMsg('visitId')}</span>
      </Box>
      <Box p={1} flexGrow={1} width={200}>
        <TextField
          name="visitId"
          value={state.visitId}
          fullWidth
          onBlur={setStateVisitId}
          onChange={e => setState({ ...state, visitId: e.target.value })}
        />
      </Box>
      <Box p={1} width={80} textAlign="end">
        <span>{locale.getMsg('fromDate')}</span>
      </Box>
      <KeyboardDatePickerWrapper
        id="validto-picker-inline"
        className="calendar-picker-input"
        value={state.fromDate}
        maxDate={state.toDate}
        maxDateMessage=""
        onChange={date => handleDateChange('fromDate', date)}
        errorProps={getErrorProps(validationErrors.fromDate)}
        disableToolbar
      />
      <Box p={1} width={80} textAlign="end">
        <span>{locale.getMsg('toDate')}</span>
      </Box>
      <KeyboardDatePickerWrapper
        id="validto-toDate-inline"
        className="calendar-picker-input"
        value={state.toDate}
        minDate={state.fromDate}
        minDateMessage={locale.getMsg('dateToBeforeDateFrom')}
        onChange={date => handleDateChange('toDate', date)}
        errorProps={getErrorProps(validationErrors.toDate)}
        disableToolbar
      />
      <Box p={1} width={30} />
      <Box p={1}>
        <Link className="cancel-link" onClick={handleClear}>
          {locale.getMsg('clear')}
        </Link>
      </Box>
      <Box p={1}>
        <IconButton type="submit" size="small" onClick={onSubmit} className="search-icon" disabled={!areDatesValid}>
          <SearchIcon />
        </IconButton>
      </Box>
    </React.Fragment>
  );
}
